import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useAuth } from "./AuthProvider";
import { useInstalledApps } from "./InstalledAppsProvider";
import helpdeskApi from "../api/helpdesk";
import api from "../api/api";

const DictionaryContext = createContext();

/**
 * DictionaryProvider
 *
 * Single source of truth for static "dictionary" data that was previously
 * refetched on every page / modal open. fetches once when the user is authenticated,
 * caches in context, and refetches on the existing `company-updated` event.
 *
 * Must mount INSIDE InstalledAppsProvider so it can skip endpoints for modules
 * that aren't installed (avoids 404s). Promise.allSettled is the safety net: a
 * failed/uninstalled endpoint simply yields an empty list instead of breaking
 * the others.
 */
const DictionaryProvider = ({ children }) => {
  const { token } = useAuth();
  const { isAppInstalled } = useInstalledApps();

  const [teams, setTeams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [knowledgeCategories, setKnowledgeCategories] = useState([]);
  const [knowledgeTags, setKnowledgeTags] = useState([]);
  const [inboxTags, setInboxTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const isFetchingRef = useRef(false);

  const list = (res) => res?.data?.results || res?.data || [];

  const fetchDictionaries = useCallback(async (forceRefresh = false) => {
    if (!token) {
      setTeams([]);
      setCategories([]);
      setKnowledgeCategories([]);
      setKnowledgeTags([]);
      setInboxTags([]);
      setLoading(false);
      return;
    }

    // Prevent concurrent fetches
    if (isFetchingRef.current && !forceRefresh) return;
    isFetchingRef.current = true;
    setLoading(true);

    const promises = [
      isAppInstalled("helpdesk") ? helpdeskApi.getTeams() : Promise.reject("uninstalled"),
      isAppInstalled("helpdesk") ? helpdeskApi.getCategories() : Promise.reject("uninstalled"),
      isAppInstalled("knowledge_system") ? api.get('/knowledge-system/categories/') : Promise.reject("uninstalled"),
      isAppInstalled("knowledge_system") ? api.get('/knowledge-system/tags/') : Promise.reject("uninstalled"),
      isAppInstalled("inbox") ? api.get('/inbox/tags/') : Promise.reject("uninstalled"),
    ];

    const [teamsRes, categoriesRes, kbCategoriesRes, kbTagsRes, inboxTagsRes] = await Promise.allSettled(promises);

    if (teamsRes.status === "fulfilled") setTeams(list(teamsRes.value));
    else if (teamsRes.reason !== "uninstalled") setTeams([]);

    if (categoriesRes.status === "fulfilled") setCategories(list(categoriesRes.value));
    else if (categoriesRes.reason !== "uninstalled") setCategories([]);

    if (kbCategoriesRes.status === "fulfilled") setKnowledgeCategories(list(kbCategoriesRes.value));
    else if (kbCategoriesRes.reason !== "uninstalled") setKnowledgeCategories([]);

    if (kbTagsRes.status === "fulfilled") setKnowledgeTags(list(kbTagsRes.value));
    else if (kbTagsRes.reason !== "uninstalled") setKnowledgeTags([]);

    if (inboxTagsRes.status === "fulfilled") setInboxTags(list(inboxTagsRes.value));
    else if (inboxTagsRes.reason !== "uninstalled") setInboxTags([]);

    isFetchingRef.current = false;
    setLoading(false);
  }, [token, isAppInstalled]);

  // Fetch on mount and when token changes (login / logout)
  useEffect(() => {
    fetchDictionaries(true);
  }, [fetchDictionaries]);

  // Refetch when a company is switched / an app is installed
  useEffect(() => {
    const handleUpdate = () => fetchDictionaries(true);
    window.addEventListener("company-updated", handleUpdate);
    return () => window.removeEventListener("company-updated", handleUpdate);
  }, [fetchDictionaries]);

  // In-memory mutator methods
  const addTeam = useCallback((team) => setTeams(prev => [...prev, team]), []);
  const updateTeam = useCallback((team) => setTeams(prev => prev.map(t => t.id === team.id ? team : t)), []);
  const deleteTeam = useCallback((id) => setTeams(prev => prev.filter(t => t.id !== id)), []);

  const addCategory = useCallback((cat) => setCategories(prev => [...prev, cat]), []);
  const updateCategory = useCallback((cat) => setCategories(prev => prev.map(c => c.id === cat.id ? cat : c)), []);
  const deleteCategory = useCallback((id) => setCategories(prev => prev.filter(c => c.id !== id)), []);

  const addKbCategory = useCallback((cat) => setKnowledgeCategories(prev => [...prev, cat]), []);
  const updateKbCategory = useCallback((cat) => setKnowledgeCategories(prev => prev.map(c => c.id === cat.id ? cat : c)), []);
  const deleteKbCategory = useCallback((id) => setKnowledgeCategories(prev => prev.filter(c => c.id !== id)), []);

  const addKbTag = useCallback((tag) => setKnowledgeTags(prev => [...prev, tag]), []);
  const updateKbTag = useCallback((tag) => setKnowledgeTags(prev => prev.map(t => t.id === tag.id ? tag : t)), []);
  const deleteKbTag = useCallback((id) => setKnowledgeTags(prev => prev.filter(t => t.id !== id)), []);

  const addInboxTag = useCallback((tag) => setInboxTags(prev => [...prev, tag]), []);
  const deleteInboxTag = useCallback((id) => setInboxTags(prev => prev.filter(t => t.id !== id)), []);

  const contextValue = useMemo(
    () => ({
      teams,
      addTeam,
      updateTeam,
      deleteTeam,
      categories,
      addCategory,
      updateCategory,
      deleteCategory,
      knowledgeCategories,
      addKbCategory,
      updateKbCategory,
      deleteKbCategory,
      knowledgeTags,
      addKbTag,
      updateKbTag,
      deleteKbTag,
      inboxTags,
      addInboxTag,
      deleteInboxTag,
      loading,
      refresh: () => fetchDictionaries(true),
    }),
    [
      teams, addTeam, updateTeam, deleteTeam,
      categories, addCategory, updateCategory, deleteCategory,
      knowledgeCategories, addKbCategory, updateKbCategory, deleteKbCategory,
      knowledgeTags, addKbTag, updateKbTag, deleteKbTag,
      inboxTags, addInboxTag, deleteInboxTag,
      loading, fetchDictionaries
    ]
  );

  return (
    <DictionaryContext.Provider value={contextValue}>
      {children}
    </DictionaryContext.Provider>
  );
};

export const useDictionary = () => useContext(DictionaryContext);

export default DictionaryProvider;
